import { Box, Typography } from '@mui/material'
import {
    MRT_ColumnFiltersState,
    useMaterialReactTable,
    type MRT_SortingState,
} from 'material-react-table'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useEntityTemplates } from '../../../CustomHooks/useEntityTemplates'
import { useUploadManager } from '../../../CustomHooks/useUploadManager'
import { UserPermissions } from '../../../constants/constants'
import { useAppSelector } from '../../../redux/hook'
import { ColumnOrderListType } from '../../../redux/slices/entitySlice'
import { exportUploadManagerToExcel } from '../../../services/uploadManagerService'
import { getNoHideEntityList } from '../../../utils/NavUtils'
import UploadManagerBulkUploadModal from './UploadManagerBulkUploadModal'
import { getUploadManagerColumns } from './columns'
import { UploadManagerHeader } from './components/UploadManagerHeader'
import { UploadManagerTable } from './components/UploadManagerTable'
import { formatUploadManagerData } from './data'
import { usePermissions } from '../../../hooks/usePermissions'

const STORAGE_KEY = 'upload-manager-columns'

const UploadManagerGrid = (): React.ReactElement => {
    const { t } = useTranslation()
    const defaultColumns: ColumnOrderListType[] = useMemo(
        () => [
            {
                id: 'createdAt',
                name: t('uploadManager.columns.date'),
                selected: true,
                columnSelection: true,
                attributeName: 'Date',
                columnName: 'date',
                attributeType: 'string',
                dataType: 'string',
                searchable: 'yes',
                attributeSourceType: null,
            },
            {
                id: 'userEmail',
                name: t('uploadManager.columns.user'),
                selected: true,
                columnSelection: true,
                attributeName: 'User',
                columnName: 'User',
                attributeType: 'string',
                dataType: 'string',
                searchable: 'yes',
                attributeSourceType: null,
            },
            {
                id: 'templateName',
                name: t('uploadManager.columns.type'),
                selected: true,
                columnSelection: true,
                attributeName: 'Type',
                columnName: 'Type',
                attributeType: 'string',
                dataType: 'string',
                searchable: 'yes',
                attributeSourceType: null,
                enableColumnFilter: true,
            },
            {
                id: 'fileName',
                name: t('uploadManager.columns.fileName'),
                selected: true,
                columnSelection: true,
                attributeName: 'fileName',
                columnName: 'File Name',
                attributeType: 'string',
                dataType: 'string',
                searchable: 'yes',
                attributeSourceType: null,
            },
            {
                id: 'progress',
                name: t('uploadManager.columns.progress'),
                selected: true,
                columnSelection: true,
                attributeName: 'progress',
                columnName: 'Progress',
                attributeType: 'Progress',
                dataType: 'progress',
                searchable: 'yes',
                attributeSourceType: null,
            },
            {
                id: 'status',
                name: t('uploadManager.columns.status'),
                selected: true,
                columnSelection: true,
                attributeName: 'status',
                columnName: 'Status',
                attributeType: 'Status',
                dataType: 'status',
                searchable: 'yes',
                attributeSourceType: null,
            },
            {
                id: 'fileUrl',
                name: t('uploadManager.columns.downloadUploadedFile'),
                selected: true,
                columnSelection: true,
                attributeName: 'fileUrl',
                columnName: 'Download File',
                attributeType: 'string',
                dataType: 'string',
                searchable: 'no',
                attributeSourceType: null,
            },
            {
                id: 'errorFileUrl',
                name: t('uploadManager.columns.errorFileDownload'),
                selected: true,
                columnSelection: true,
                attributeName: 'errorFileUrl',
                columnName: 'Error File',
                attributeType: 'string',
                dataType: 'string',
                searchable: 'no',
                attributeSourceType: null,
            },
        ],
        [t],
    )
    const { hasPermission } = usePermissions()
    const hasWriteAccess = useMemo(
        () => hasPermission([UserPermissions.ORG_ADMIN]),
        [hasPermission],
    )

    const orgId = useAppSelector((state) => state.org.org?.id)

    const [searchBy, setSearchBy] = useState('TEMPLATE')
    const [searchQuery, setSearchQuery] = useState('')
    const [uploadModalOpen, setUploadModalOpen] = useState(false)
    const [modalMode, setModalMode] = useState<'upload' | 'download' | 'both'>(
        'upload',
    )
    const [sorting, setSorting] = useState<MRT_SortingState>([
        { id: 'createdAt', desc: true },
    ])
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>(
        [],
    )

    const tableContainerRef = useRef<HTMLDivElement>(null)

    const initialSelectedColumns = useMemo(() => {
        const saved = localStorage.getItem(STORAGE_KEY)
        if (saved) {
            return JSON.parse(saved)
        }
        return defaultColumns
    }, [defaultColumns])

    const [columnVisibilityList, setColumnVisibilityList] = useState<
        ColumnOrderListType[]
    >(initialSelectedColumns)

    const [savedColumnVisibilityList, setSavedColumnVisibilityList] = useState<
        ColumnOrderListType[]
    >(initialSelectedColumns)

    const onSaveColumnVisibility = useCallback(
        (cols: ColumnOrderListType[]) => {
            setSavedColumnVisibilityList(cols)
            localStorage.setItem(STORAGE_KEY, JSON.stringify(cols))
            enqueueSnackbar(t('uploadManager.notifications.columnsSaved'), {
                variant: 'success',
            })
        },
        [t],
    )

    const onResetColumnVisibility = useCallback(() => {
        setColumnVisibilityList(savedColumnVisibilityList)
        enqueueSnackbar(t('uploadManager.notifications.columnsReset'), {
            variant: 'success',
        })
    }, [savedColumnVisibilityList, t])

    const columnVisibility = useMemo(() => {
        const visibility: Record<string, boolean> = {}
        columnVisibilityList.forEach((c) => {
            visibility[c.id] = c.selected
        })
        return visibility
    }, [columnVisibilityList])

    const handleExportEntities = useCallback(
        async (selectedColumnIds: string[]) => {
            if (!orgId) return

            const response = await exportUploadManagerToExcel(
                orgId,
                { searchBy, searchQuery },
                selectedColumnIds,
            )

            const blob = new Blob([response.data], {
                type:
                    response.headers['content-type'] ||
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            })

            const url = globalThis.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url

            const fileName =
                response.headers['content-disposition']
                    ?.split('filename=')[1]
                    ?.replace(/"/g, '') || 'upload-manager-export.xlsx'

            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()
            link.remove()
            globalThis.URL.revokeObjectURL(url)
        },
        [orgId, searchBy, searchQuery],
    )
    const { data, fetchNextPage, isFetching, status, hasNextPage, refetch } =
        useUploadManager(
            orgId,
            sorting[0]?.id ?? 'createdAt',
            sorting[0]?.desc ? 'DESC' : 'ASC',
            searchBy?.toUpperCase(),
            searchQuery,
            columnFilters,
        )
    const { data: templatesData } = useEntityTemplates(orgId)

    const templates = useMemo(() => {
        const filteredTemplates = getNoHideEntityList(templatesData ?? [])
        return (
            filteredTemplates.map((t) => ({
                id: t.id,
                name: t.name,
                subEntityTypes: t.subEntityTypes,
            })) ?? []
        )
    }, [templatesData])

    const allRows = useMemo(() => formatUploadManagerData(data), [data])

    const totalRecords = data?.pages[0]?.totalElements ?? 0

    const statusColors: Record<string, string> = useMemo(
        () => ({
            completed: '#4caf50',
            processing: '#2196f3',
            reupload_required: '#ff9800',
            failed: '#f44336',
            completed_with_errors: '#ff9800',
        }),
        [],
    )

    const columns = useMemo(
        () => getUploadManagerColumns(statusColors, t),
        [t, statusColors],
    )

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } =
                    containerRefElement
                if (
                    clientHeight > 0 &&
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    hasNextPage &&
                    allRows.length < totalRecords
                ) {
                    fetchNextPage()
                }
            }
        },
        [fetchNextPage, isFetching, hasNextPage, allRows.length, totalRecords],
    )

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current)
    }, [fetchMoreOnBottomReached])

    const icons = useMemo(
        () => ({
            ArrowDownwardIcon: (props: any) => {
                return (
                    <Box {...props}>
                        <Box sx={{ fontSize: '14px', lineHeight: 1 }}>▼</Box>
                    </Box>
                )
            },
        }),
        [],
    )

    const renderEmptyRowsFallback = useCallback(
        () => (
            <Typography sx={{ mt: 4, textAlign: 'center' }}>
                {t('uploadManager.table.noRecords')}
            </Typography>
        ),
        [t],
    )

    const table = useMaterialReactTable({
        columns,
        data: allRows,
        enablePagination: false,
        enableColumnActions: false,
        enableColumnFilters: true,
        manualFiltering: true,
        columnFilterDisplayMode: 'popover',
        enableRowVirtualization: true,
        enableColumnResizing: true,
        layoutMode: 'grid',
        columnResizeMode: 'onChange',
        enableRowSelection: false,
        manualSorting: true,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        enableStickyHeader: true,
        enableTopToolbar: false,
        enableBottomToolbar: false,
        enableColumnPinning: true,
        muiTablePaperProps: {
            sx: {
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            },
        },
        muiTableContainerProps: {
            ref: tableContainerRef,
            onScroll: (event: React.UIEvent<HTMLDivElement>) =>
                fetchMoreOnBottomReached(event.currentTarget),
            sx: {
                height: '100%',
            },
        },
        initialState: {
            columnPinning: {
                left: [],
            },
        },
        muiTableBodyCellProps: {
            sx: {
                typography: 'body1',
                '&[data-pinned="true"]': {
                    boxShadow: 'none !important',
                    position: 'sticky',
                    zIndex: 2,
                    backgroundColor: '#fff',

                    '&::before': {
                        backgroundColor: 'transparent !important',
                    },
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                backgroundColor: 'rgb(245, 251, 255);',
                typography: 'body1',
                '& .Mui-TableHeadCell-Content': {
                    fontWeight: 600,
                },

                '& .Mui-TableHeadCell-ResizeHandle-Wrapper': {
                    mr: '-8px',
                },
                '&.MuiTableCell-stickyHeader': {
                    pl: '2px !important',
                },
                '& .MuiDivider-root.MuiDivider-vertical.MuiDivider-flexItem.Mui-TableHeadCell-ResizeHandle-Divider':
                    {
                        backgroundColor: 'rgb(204, 204, 204)',
                        borderWidth: '1.5px',
                        borderRadius: '1px',
                    },
                '&[data-pinned="true"]': {
                    boxShadow: 'none !important',

                    '&::before': {
                        backgroundColor: 'transparent !important',
                    },
                },
            },
        },

        state: {
            isLoading: status === 'pending',
            showAlertBanner: status === 'error',
            showProgressBars: isFetching,
            showColumnFilters: true,
            columnFilters,
            columnVisibility,
            sorting,
        },
        icons,
        renderEmptyRowsFallback,
    })

    const handleUploadClick = useCallback(() => {
        setModalMode('both')
        setUploadModalOpen(true)
    }, [])

    const handleModalClose = useCallback(() => setUploadModalOpen(false), [])

    const handleSearchAction = useCallback((text?: string) => {
        setSearchQuery(text ?? '')
    }, [])

    return (
        <Box sx={{ width: '100%', height: '100%' }}>
            <UploadManagerHeader
                searchBy={searchBy}
                setSearchBy={setSearchBy}
                filteredRowsCount={allRows.length}
                totalRecords={totalRecords}
                onUploadClick={handleUploadClick}
                columnVisibilityList={columnVisibilityList}
                onUpdateColumns={setColumnVisibilityList}
                onSaveColumns={onSaveColumnVisibility}
                onResetColumns={onResetColumnVisibility}
                handleExportEntities={handleExportEntities}
                enableBulkUpload={hasWriteAccess}
                onSearchAction={handleSearchAction}
            />

            <UploadManagerTable table={table} />

            <UploadManagerBulkUploadModal
                open={uploadModalOpen}
                onClose={handleModalClose}
                templates={templates}
                orgId={orgId ?? ''}
                mode={modalMode}
                showSubTypes={true}
                onUploadSuccess={refetch}
            />
        </Box>
    )
}

export default UploadManagerGrid
